import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Megaphone, Pencil, Pin, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, ApiError, type AdminAnnouncement, type AnnouncementAudience, type AnnouncementInputBody } from "@/lib/api";
import { intlLocale } from "@/lib/i18n";

const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Fehler") : "Fehler");
const deDateTime = (iso: string) => new Date(iso).toLocaleString(intlLocale(), { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });
const plusDays = (ymd: string, n: number) => new Date(Date.parse(`${ymd}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** Logiful intern: News & Ankündigungen pflegen, Anliefertermine aller Kunden */
export function AdminNewsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-announcements"], queryFn: api.admin.announcements });
  const [editing, setEditing] = useState<AdminAnnouncement | "new" | null>(null);
  const del = useMutation({
    mutationFn: (id: string) => api.admin.deleteAnnouncement(id),
    onSuccess: () => { toast.success("Gelöscht"); void qc.invalidateQueries({ queryKey: ["admin-announcements"] }); },
    onError: (e) => toast.error("Nicht gelöscht", { description: errMsg(e) }),
  });
  const now = Date.now();
  const state = (a: AdminAnnouncement) => (Date.parse(a.publishAt) > now ? { label: "geplant", tone: "neutral" as const } : a.visibleUntil && Date.parse(a.visibleUntil) <= now ? { label: "abgelaufen", tone: "neutral" as const } : { label: "sichtbar", tone: "success" as const });
  const audienceText = (a: AnnouncementAudience) => (a.type === "all" ? "alle Kunden" : a.type === "warehouses" ? `Kunden in ${a.ids.length} Lager${a.ids.length > 1 ? "n" : ""}` : `${a.ids.length} Kunde${a.ids.length > 1 ? "n" : ""}`);

  return (
    <div className="pb-16">
      <PageHeader title="News & Termine" description="Ankündigungen auf der Übersicht der Kunden pflegen und alle festen Anliefertermine im Blick behalten." actions={<Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing("new")}>Neue Ankündigung</Button>} />
      <div className="px-4 md:px-8 space-y-6 max-w-5xl">
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-border font-semibold text-[14px] flex items-center gap-2"><Megaphone className="size-4 text-fg-muted" /> Ankündigungen</div>
          {q.isLoading ? <div className="p-4"><Skeleton className="h-20" /></div> : !q.data?.length ? (
            <EmptyState icon={<Megaphone className="size-5" />} title="Noch keine Ankündigungen">Z. B. Inventur, Feiertagsregelungen, neue Funktionen – erscheint als Kachel auf der Übersicht der Kunden.</EmptyState>
          ) : (
            <ul className="divide-y divide-border">
              {q.data.map((a) => {
                const st = state(a);
                return (
                  <li key={a.id} className="px-4 py-3 flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-[13.5px]">{a.title}</span>
                        {a.pinned && <Pin className="size-3.5 text-fg-subtle" />}
                        {a.level === "important" && <Badge tone="warning">Wichtig</Badge>}
                        <Badge tone={st.tone}>{st.label}</Badge>
                      </div>
                      {a.body && <p className="mt-0.5 text-[12.5px] text-fg-muted line-clamp-2 whitespace-pre-line">{a.body}</p>}
                      <p className="mt-1 text-[11.5px] text-fg-subtle">{audienceText(a.audience)} · ab {deDateTime(a.publishAt)}{a.visibleUntil ? ` bis ${deDateTime(a.visibleUntil)}` : ""}</p>
                    </div>
                    <button onClick={() => setEditing(a)} className="size-8 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={`${a.title} bearbeiten`}><Pencil className="size-3.5" /></button>
                    <button onClick={() => { if (confirm(`„${a.title}“ löschen?`)) del.mutate(a.id); }} className="size-8 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-danger" aria-label={`${a.title} löschen`}><Trash2 className="size-3.5" /></button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
        <AppointmentsCard />
      </div>
      {editing && <AnnouncementDialog item={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function AppointmentsCard() {
  const [days, setDays] = useState(14);
  const from = today(), to = plusDays(from, days);
  const q = useQuery({ queryKey: ["admin-appointments", from, days], queryFn: () => api.admin.inboundAppointments(from, to) });
  let lastDate = "";
  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <span className="font-semibold text-[14px] flex items-center gap-2"><CalendarClock className="size-4 text-fg-muted" /> Feste Anliefertermine aller Kunden</span>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Zeitraum" className="ml-auto h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px]">
          {[7, 14, 30, 60].map((d) => <option key={d} value={d}>nächste {d} Tage</option>)}
        </select>
      </div>
      {q.isLoading ? <div className="p-4"><Skeleton className="h-16" /></div> : !q.data?.length ? (
        <p className="px-4 py-6 text-[13px] text-fg-muted text-center">Keine festen Termine in diesem Zeitraum. Termine tragen Kunden oder Logiful am Wareneingang ein.</p>
      ) : (
        <table className="w-full text-[13px]">
          <tbody>
            {q.data.map((a) => {
              const head = a.date !== lastDate;
              lastDate = a.date;
              return (
                <tr key={a.id} className={cx("border-t border-border", head && "border-t-border-strong")}>
                  <td className="pl-4 pr-3 py-2 whitespace-nowrap align-top text-fg-muted w-32">{head ? new Date(`${a.date}T12:00:00Z`).toLocaleDateString(intlLocale(), { weekday: "short", day: "2-digit", month: "2-digit" }) : ""}</td>
                  <td className="px-3 py-2 whitespace-nowrap align-top font-medium tabular-nums">{a.from}{a.to ? `–${a.to}` : ""}</td>
                  <td className="px-3 py-2 align-top">
                    <span className="font-medium">{a.tenantName}</span>{a.customerNumber ? <span className="text-fg-subtle"> · Kd. {a.customerNumber}</span> : null}
                    <span className="block text-[12px] text-fg-muted">{a.number}{a.supplier ? ` · ${a.supplier}` : ""} · {a.lines} Pos., {a.units.toLocaleString(intlLocale())} Stück{a.note ? ` · ${a.note}` : ""}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function AnnouncementDialog({ item, onClose }: { item: AdminAnnouncement | null; onClose: () => void }) {
  const qc = useQueryClient();
  const wh = useQuery({ queryKey: ["admin-delivery-addresses"], queryFn: api.admin.deliveryAddresses });
  const tenants = useQuery({ queryKey: ["admin-tenants"], queryFn: api.admin.tenants });
  const local = (iso: string | null) => (iso ? new Date(new Date(iso).getTime() - new Date(iso).getTimezoneOffset() * 60_000).toISOString().slice(0, 16) : "");
  const [v, setV] = useState({
    title: item?.title ?? "",
    body: item?.body ?? "",
    level: item?.level ?? ("info" as const),
    pinned: item?.pinned ?? false,
    audience: item?.audience ?? ({ type: "all" } as AnnouncementAudience),
    publishAt: item ? local(item.publishAt) : "",
    visibleUntil: local(item?.visibleUntil ?? null),
  });
  const body = (): AnnouncementInputBody => ({ title: v.title, body: v.body, level: v.level, pinned: v.pinned, audience: v.audience, publishAt: v.publishAt ? new Date(v.publishAt).toISOString() : null, visibleUntil: v.visibleUntil ? new Date(v.visibleUntil).toISOString() : null });
  const save = useMutation({
    mutationFn: () => (item ? api.admin.updateAnnouncement(item.id, body()) : api.admin.createAnnouncement(body())),
    onSuccess: () => { toast.success(item ? "Gespeichert" : "Veröffentlicht"); void qc.invalidateQueries({ queryKey: ["admin-announcements"] }); void qc.invalidateQueries({ queryKey: ["announcements"] }); onClose(); },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }),
  });
  const ids = v.audience.type === "all" ? [] : v.audience.ids;
  const toggle = (id: string) => setV({ ...v, audience: { type: v.audience.type as "warehouses" | "tenants", ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] } as AnnouncementAudience });
  const options = v.audience.type === "warehouses" ? (wh.data?.warehouses ?? []).map((w) => ({ id: w.id, label: w.name || w.id })) : v.audience.type === "tenants" ? (tenants.data ?? []).map((t) => ({ id: t.id, label: t.name })) : [];

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-surface p-5 shadow-2xl animate-pop-in">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold">{item ? "Ankündigung bearbeiten" : "Neue Ankündigung"}</Dialog.Title>
              <Dialog.Description className="text-fg-muted text-[13px]">Erscheint als Kachel auf der Übersicht der gewählten Kunden.</Dialog.Description>
            </div>
            <Dialog.Close className="size-8 grid place-items-center rounded-lg hover:bg-surface-2" aria-label="Schließen"><X className="size-4" /></Dialog.Close>
          </div>
          <div className="mt-4 space-y-3">
            <label className="block"><span className="text-[12.5px] font-medium">Titel</span><Input className="mt-1" value={v.title} maxLength={140} onChange={(e) => setV({ ...v, title: e.target.value })} placeholder="z. B. Inventur am 30. Dezember" /></label>
            <label className="block"><span className="text-[12.5px] font-medium">Text</span>
              <textarea rows={5} maxLength={4000} value={v.body} onChange={(e) => setV({ ...v, body: e.target.value })} placeholder="Was, wann, was müssen Kunden tun? Links werden klickbar." className="mt-1 w-full rounded-lg border border-border bg-surface p-2.5 text-[13px]" />
            </label>
            <div className="flex flex-wrap items-center gap-4 text-[13px]">
              <label className="inline-flex items-center gap-2"><input type="radio" checked={v.level === "info"} onChange={() => setV({ ...v, level: "info" })} className="accent-ink" /> Info</label>
              <label className="inline-flex items-center gap-2"><input type="radio" checked={v.level === "important"} onChange={() => setV({ ...v, level: "important" })} className="accent-ink" /> Wichtig (hervorgehoben)</label>
              <label className="inline-flex items-center gap-2 ml-auto"><input type="checkbox" checked={v.pinned} onChange={(e) => setV({ ...v, pinned: e.target.checked })} className="accent-ink" /> Oben anheften</label>
            </div>
            <div>
              <span className="text-[12.5px] font-medium">Für wen?</span>
              <div className="mt-1 flex rounded-lg border border-border p-0.5 text-[12.5px] w-fit">
                {([["all", "Alle Kunden"], ["warehouses", "Kunden eines Lagers"], ["tenants", "Einzelne Kunden"]] as const).map(([k, l]) => (
                  <button key={k} className={cx("px-2.5 h-7 rounded-md", v.audience.type === k && "bg-surface-2 font-medium")} onClick={() => setV({ ...v, audience: k === "all" ? { type: "all" } : { type: k, ids: [] } as unknown as AnnouncementAudience })}>{l}</button>
                ))}
              </div>
              {v.audience.type !== "all" && (
                <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-border p-2 grid sm:grid-cols-2 gap-1">
                  {options.length === 0 && <span className="text-[12.5px] text-fg-muted">Lade …</span>}
                  {options.map((o) => (
                    <label key={o.id} className="flex items-center gap-2 text-[13px] px-1 py-0.5 rounded hover:bg-surface-2">
                      <input type="checkbox" checked={ids.includes(o.id)} onChange={() => toggle(o.id)} className="accent-ink" /> <span className="truncate">{o.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-[12.5px] font-medium">Sichtbar ab</span><Input type="datetime-local" className="mt-1" value={v.publishAt} onChange={(e) => setV({ ...v, publishAt: e.target.value })} /><span className="text-[11.5px] text-fg-subtle">leer = sofort</span></label>
              <label className="block"><span className="text-[12.5px] font-medium">Sichtbar bis</span><Input type="datetime-local" className="mt-1" value={v.visibleUntil} onChange={(e) => setV({ ...v, visibleUntil: e.target.value })} /><span className="text-[11.5px] text-fg-subtle">leer = bis gelöscht</span></label>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button onClick={onClose}>Abbrechen</Button>
            <Button variant="primary" disabled={v.title.trim().length < 3 || (v.audience.type !== "all" && !ids.length)} loading={save.isPending} onClick={() => save.mutate()}>{item ? "Speichern" : "Veröffentlichen"}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
